import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { NCBAccount } from '@nocodebackend/account-react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiDatabase, FiLogOut, FiShield, FiTool, FiSun, FiMoon, FiUser, FiMenu, FiShoppingBag } = FiIcons;

const Layout = ({ children }) => {
  const { pendingNLACount } = useAppContext();
  const { user, profile, signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const isAdmin = profile?.role === 'admin';
  
  const navItems = [
    { path: '/', label: 'Search', icon: FiSearch },
    { path: '/shop', label: 'Shop', icon: FiShoppingBag },
    { path: '/account', label: 'Account', icon: FiUser },
    ...(isAdmin ? [{ path: '/admin', label: 'Management', icon: FiDatabase, badge: pendingNLACount }] : [])
  ];

  if (['/login', '/register', '/reset-password', '/auth/callback'].includes(location.pathname)) {
    return <div className="min-h-screen bg-slate-100 dark:bg-slate-950">{children}</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-300 font-sans selection:bg-blue-200">
      <header className="sticky top-0 z-50 bg-navy border-b-2 border-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-stretch justify-between h-14">
            {/* Wordmark */}
            <Link to="/" className="flex items-center gap-3 shrink-0">
              <div className="bg-blue-600 p-1.5">
                <SafeIcon icon={FiTool} className="w-4 h-4 text-white" />
              </div>
              <span className="font-cond text-lg font-semibold uppercase tracking-wider text-white">
                Part<span className="text-blue-300">Scouter</span>
              </span>
            </Link>

            {/* Primary navigation */}
            <nav className="hidden md:flex items-stretch gap-1 mx-6 self-stretch" aria-label="Primary">
              {navItems.map(item => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={isActive ? 'page' : undefined}
                    className={`relative flex items-center gap-2 px-4 text-sm font-semibold border-b-2 -mb-0.5 ${
                      isActive
                        ? 'text-white border-blue-300 bg-white/5'
                        : 'text-slate-300 border-transparent hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <SafeIcon icon={item.icon} className="w-4 h-4" />
                    {item.label}
                    {item.badge > 0 && (
                      <span className="ml-1 inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-red-600 px-1 text-[11px] text-white">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Actions & account */}
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={toggleTheme}
                aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
                className="flex h-11 w-11 items-center justify-center text-slate-300 hover:text-white hover:bg-white/10 md:h-9 md:w-9"
              >
                <SafeIcon icon={isDark ? FiSun : FiMoon} className="w-4 h-4" />
              </button>

              {user && (
                <div className="flex items-center gap-3 pl-2 sm:pl-4 border-l border-white/15">
                  <div className="hidden lg:block text-right">
                    <div className="font-cond text-[11px] font-semibold uppercase tracking-wider text-blue-300 leading-none mb-1">
                      {profile?.subscription_status === 'pro' ? 'Pro plan' : 'Free plan'}
                    </div>
                    <div className="text-xs font-semibold text-white truncate max-w-[140px]">
                      {profile?.full_name}
                    </div>
                  </div>

                  {/* Official NoCodeBackend account menu: profile, security, devices, billing */}
                  <NCBAccount />

                  <button
                    onClick={handleLogout}
                    aria-label="Sign out"
                    className="flex h-11 items-center gap-2 px-3 border border-white/25 text-slate-200 hover:bg-white/10 hover:text-white text-xs font-semibold md:h-9"
                  >
                    <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                    <span className="hidden sm:inline">Sign out</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 pb-24 md:pb-6">
        {children}
      </main>

      {/* Phone navigation: thumb-reach tabs along the bottom edge */}
      <nav
        aria-label="Primary"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 grid bg-navy border-t border-white/15 pb-[env(safe-area-inset-bottom)]"
        style={{ gridTemplateColumns: `repeat(${navItems.length}, minmax(0, 1fr))` }}
      >
        {navItems.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              aria-current={isActive ? 'page' : undefined}
              className={`relative flex min-h-[56px] flex-col items-center justify-center gap-1 text-[11px] font-semibold ${
                isActive ? 'text-white bg-white/10 border-t-2 border-blue-300 -mt-px' : 'text-slate-300'
              }`}
            >
              <SafeIcon icon={item.icon} className="w-5 h-5" />
              {item.label}
              {item.badge > 0 && (
                <span className="absolute top-1.5 left-1/2 ml-2 inline-flex min-w-[1.1rem] h-[1.1rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] text-white">
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default Layout;
